import React, { useState, useContext, useEffect } from 'react';
import { SdfBadge, SdfBox, SdfButton, SdfIcon, SdfSpotIllustration, SdfTooltip, SdfAlert } from '@synerg/react-components';
import axios from "axios";
import { get, uniqueId } from "lodash";
import moment from 'moment-timezone';

import { useApplicationState, upsertToasts } from '../../../utilities/applicationState';
import { ApiPickerContext } from '..';
import getDevPortalContext from "../../../utilities/devPortalContext";

const VERB_TO_STATUS = {
	"GET": "in-progress",
	"POST": "complete",
	"PUT": "changed",
	"DELETE": "failed"
};

const getConsentText = async (finalCanonicalList, existingScopes, consentTexts = []) => {
	if (finalCanonicalList.length === 0) return [];

	const { data: { canonicalConsentTexts = [] } } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/canonical-consent-texts`, {
		canonicalsToSearch: [...finalCanonicalList, ...existingScopes].filter(Boolean),
		existingConsentTexts: consentTexts.filter(Boolean)
	});

	return canonicalConsentTexts;
};

export default function ShoppingCart({
	addApis, addEvents, handleRemoveApis, handleRemoveEvents, projectIdEncrypted, consumerApplicationID,
	filteredApis, subscribedEvents, closeModal, resetShoppingCart, setToast, handleError, addLinkRef,
	productCode, missingApis, setMissingApis, scopeLastModified, search
}) {
	const [ isLoading, setIsLoading ] = useState(false);
	const [ newConsentTexts, setNewConsentTexts ] = useState([]);
	const [ canonicalApis, setCanonicalApis ] = useState({});
	const [proposedFinalTexts, setProposedFinalTexts] = useState([]);

	const [, dispatch] = useApplicationState();
	const { projectDetails, apiPickerFetchProjectDetails, clientOOID, b2bScopes, consentTexts } = useContext(ApiPickerContext);
	let needApproval = newConsentTexts.length > 0;
	if (projectDetails.status === 'CLIENT_APP_CREATED') {
		needApproval = false;
	}

	const existingApis = filteredApis.map((existingApi) => ({
		httpMethod: existingApi.verb.toLowerCase(),
		uri: existingApi.path,
		canonicalUri: existingApi.canonical,
		text: get(existingApi,'text',''),
		type: get(existingApi, 'type', ''),
		description: get(existingApi, 'name', ''),
		domain: existingApi.domain,
		subCategory: existingApi.subCategory
	}));

	const addApisList = addApis.map((api) => ({
		httpMethod: api.httpMethod,
		uri: api.uri,
		canonicalUri: api.canonicalUri,
		text: get(api, 'text', ''),
		type: get(api, 'type', ''),
		description: get(api, 'description', ''),
		domain: api.domain,
		subCategory: api.subCategory
	})).filter((api) => !existingApis.some(({ uri, httpMethod }) => uri === api.uri && httpMethod === api.httpMethod));

	const existingEvents = subscribedEvents.map((event) => ({
		code: event.code,
		description: event.description,
		canonicalUri: event.canonicalUri,
		domain: event.domain,
		subCategory: event.subCategory
	}));

	const addEventsList = addEvents.map((event) => ({
		code: event.code,
		description: event.description,
		canonicalUri: event.canonicalUri,
		domain: event.domain,
		subCategory: event.subCategory
	})).filter((event) => !existingEvents.some(({ canonicalUri }) => canonicalUri === event.canonicalUri));

	const addedApisCanonicalList = addApisList.map((api) => api.canonicalUri)
		.filter((item, index, self) => self.indexOf(item) === index);

	const finalAddedCanonicalList = [
		...addedApisCanonicalList,
		...addEventsList.map((event) => event.canonicalUri)
	].filter((item, index, self) => self.indexOf(item) === index);


	const checkConsentTexts = async (finalCanonicals, b2bScopes, consentTexts) => {
		if (!clientOOID) return;
		const receivedConsentTexts = await getConsentText(finalCanonicals, b2bScopes, consentTexts);
		const missingConsentText = receivedConsentTexts.filter((text) => !consentTexts.includes(text));
		setProposedFinalTexts(receivedConsentTexts);
		setNewConsentTexts(missingConsentText);
	};

	useEffect(() => {
		const fetchCanonicalApis = async () => {
			let allCanonicalApis = { ...canonicalApis };
			const knownCanonicals = Object.keys(canonicalApis);
			const canonicalList = addedApisCanonicalList.filter(item => !knownCanonicals.includes(item));

			if (canonicalList.length > 0) {
				try {
					const response = await getDevPortalContext().post(`${import.meta.env.VITE_API_V2_PATH}/${productCode}/operations`, {
						canonicalList
					});

					// const response = testCall(`${import.meta.env.VITE_API_EXTERNAL_URL}${import.meta.env.VITE_API_V2_PATH}/${productCode}/operations`, { canonicalList });

					const newCanonicalApis = response.data.operations.reduce((acc, api) => {
						const formattedApi = {
							httpMethod: api.httpMethod,
							uri: api.uri,
							canonicalUri: api.canonicalUri,
							text: api.text,
							type: api.type,
							description: api.description,
							domain: api.domain,
							subCategory: api.subCategory
						}
						if (acc[formattedApi.canonicalUri]) {
							acc[formattedApi.canonicalUri] = [...acc[formattedApi.canonicalUri], formattedApi];
						} else {
							acc[formattedApi.canonicalUri] = [formattedApi];
						}
						return acc;
					}, {});

					allCanonicalApis = { ...canonicalApis, ...newCanonicalApis };
				} catch (e) {
					const errorMessage = get(e, 'response.data.message', 'Error Fetching Additional Canonical APIs');
					handleError(errorMessage);
				}
			}

			const newMissingApis = addApisList.map((api) => api.canonicalUri)
				.filter((item, index, self) => self.indexOf(item) === index)
				.reduce((acc, canonical) => {
					if (allCanonicalApis[canonical]) {
						return [ ...acc, ...allCanonicalApis[canonical]] ;
					}
					return acc;
				}, [])
				.filter((api) => !addApisList.some(({ uri, httpMethod }) => uri === api.uri && httpMethod === api.httpMethod));

			if (newMissingApis.length > missingApis.length) {
				dispatch(upsertToasts({
					key: "shopping-cart-auto-add-apis",
					status: "info",
					message: "Be advised! You have selected an API that belongs to a pre-bundled set. As a result, more than one API has been selected on your behalf and placed along the right sidebar to be added to your project.",
					autoCloseAfter: 30000
				}));
			}
			setCanonicalApis(allCanonicalApis);
			setMissingApis(newMissingApis);
		};


		fetchCanonicalApis();
		checkConsentTexts(finalAddedCanonicalList, b2bScopes, consentTexts);

	}, [addApis, filteredApis]);


	useEffect(() => {
		checkConsentTexts(finalAddedCanonicalList, b2bScopes, consentTexts);
	}, [addEvents, subscribedEvents]);


	const displayedApis = [...addApisList, ...missingApis];

	const refreshProjectConsumerDetails = async () => {
		await apiPickerFetchProjectDetails(true);
	}

	const submitScopeChange = async () => {
		setToast(false)
		setIsLoading(true);
		try {
			const proposedApis = [...displayedApis, ...existingApis];
			const proposedEvents = [...addEventsList, ...existingEvents];
			const proposedConsentTexts = (needApproval || newConsentTexts) ? proposedFinalTexts : consentTexts;
			let hasSubscriptions = b2bScopes.length > 0;
			if (projectDetails.status === 'CLIENT_APP_CREATED') {
				hasSubscriptions = false;
			}
			const cutOffDate = needApproval ? moment().add(1, 'year').format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
			const emailData = {
				clientOOID,
				addedApis: displayedApis.map(({ text }) => text),
				addedEventSubscriptions: addEventsList.map(({ code }) => code)
			};

			// Filter out api if path is `/auth/oauth/v2/token` from proposedApis
			const proposedApisWithoutToken = proposedApis.filter(({uri}) => uri !== '/auth/oauth/v2/token');

			const proposedScopes = [...proposedApisWithoutToken, ...proposedEvents].map(({ canonicalUri }) => canonicalUri)
				.filter((uri, index, self) => self.indexOf(uri) === index);

			await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/si/change-request`, {
				projectID: projectIdEncrypted,
				consumerApplicationID,
				proposedScopes,
				scopeLastModified,
				proposedConsentTexts,
				cutOffDate,
				hasSubscriptions,
				emailData,
			});


			await apiPickerFetchProjectDetails();

			closeModal();

			const needApprovalMessage = "will be added to the project upon approval from the client."
			const addedToProjectMessage = `${(addEventsList.length + displayedApis.length) > 1 ? "were" : "was"} successfully added to your project.`

			const toastData = {
				key: `shopping-cart-success-toast-${uniqueId()}`,
				autoCloseAfter: 30000,
				status: "success",
				message: `Congrats! ${displayedApis.length > 0 ? `${displayedApis.length} API${displayedApis.length > 1 ? "s" : ""}` : ""}
					${displayedApis.length > 0 && addEventsList.length > 0 ? " and " : ""}
					${addEventsList.length > 0 ? `${addEventsList.length} Event Subscription${addEventsList.length > 1 ? "s" : ""}` : ""} 
					${needApproval ? needApprovalMessage : addedToProjectMessage}`
			};
			dispatch(upsertToasts(toastData));
			resetShoppingCart();
		} catch (e) {
			const status = get(e, 'response.status');
			if (status === 409) {
				await refreshProjectConsumerDetails();
			}
			const errorMessage = get(e, 'response.data.message', 'Error Modifying Scopes');
			handleError(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="bg-action-default-100 h-full shopping-cart-width -mt-4 pb-4">
			<SdfBox
				className="flex flex-col h-full"
				layout="vertical"
				variant="filled"
				fill="shaded"
				shape="squared"
			>
				{needApproval && (
					<SdfAlert status="warning" type="banner" size="sm" className='py-4'>
						<span>These APIs require approval from the client</span>
					</SdfAlert>
				)}
				<div className={`text-heading-05 font-bold${needApproval ? "" : " pt-4"}`}>My Selections</div>
				<div className={`flex flex-col min-h-0 ${displayedApis.length === 0 ? " shopping-cart-border" : ""}`}>
					<div className="bg-action-default-100 shopping-cart-width text-heading-04 font-bold py-4">
						APIs ({displayedApis.length})
					</div>
					<div className="flex-initial block overflow-y-scroll">
						{displayedApis.map((api, index) => {
							const method = get(api, 'httpMethod', '').toUpperCase();
							const isMissing = missingApis.some(({ uri, httpMethod }) => uri === api.uri && httpMethod === api.httpMethod);
							return (
								<div
									key={`add-apis-${index}`}
									className='h-auto discovery-text-wrap flex items-baseline shopping-cart-border mb-1 justify-between'
								>
									<div className="flex flex-col break-all flex-grow">
											<span className="font-semibold text-sm">
												<span className={`${isMissing ? "text-disabled" : ""}`}>{api.text}</span>
												{isMissing ?
													<SdfTooltip
														positionContext="MOUSE"
														state="neutral"
														className="relative"
													>
														<SdfIcon className="cursor-pointer" icon="alert-info" slot="tooltip-target"/>
														<div className="text-wrap break-normal">
															This API was automatically selected because it belongs to a pre-bundled set of APIs.
														</div>
													</SdfTooltip> :
													""
												}
											</span>
										<div className={`text-sm mb-4${isMissing ? " text-disabled" : ""}`}>{api.uri}</div>
									</div>
									<div className="flex flex-row float-right justify-between shopping-cart-badge items-center">
										<SdfBadge
											spacing={"tight"} className='mr-1'
											status={VERB_TO_STATUS[method] || "default"}
										>
											{method}
										</SdfBadge>
										<div className={`no-underline hover:no-underline text-tertiary${isMissing ? " invisible" : ""}`}>
											<SdfButton
												className={"no-underline hover:no-underline"}
												emphasis={"tertiary"}
												onClick={() => handleRemoveApis(api)}
												disabled={isMissing ? true : undefined}
											>
												<SdfIcon role="presentation"
													className="cursor-pointer text-tertiary no-underline hover:no-underline"
													icon="action-close"
												/>
											</SdfButton>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
				{displayedApis.length === 0 ? <div className="flex flex-col min-h-0" /> : ""}
				<div className="flex flex-col min-h-0">
					<div className="bg-action-default-100 shopping-cart-width text-heading-04 font-bold py-4">
						Event Subscriptions ({addEventsList.length})
					</div>
					<div className="flex-initial block overflow-y-scroll">
						{
							addEventsList.map((event, index) => {
								return (
									<div
										key={`add-event-${index}`}
										className='h-auto discovery-text-wrap flex items-baseline shopping-cart-border mb-1 justify-between'
									>
										<div className="flex flex-col w-9/12 break-all">
											<div className="font-semibold text-sm">{event.code}</div>
											<div className='text-sm mb-4'>{event.canonicalUri}</div>
										</div>
										<div className="flex flex-row float-right justify-between shopping-cart-badge items-center">
											<div className="no-underline hover:no-underline text-tertiary">
												<SdfButton
													className={"no-underline hover:no-underline"}
													emphasis={"tertiary"}
													onClick={() => handleRemoveEvents(event)}
												>
													<SdfIcon role="presentation"
														className="cursor-pointer text-tertiary no-underline hover:no-underline"
														icon="action-close"
													/>
												</SdfButton>
											</div>
										</div>
									</div>
								);
							})
						}
					</div>
				</div>
				{displayedApis.length === 0 && addEventsList.length === 0 ?
					<div className='flex flex-wrap-reverse max-h-sm'>
						<div className={'mt-12 flex flex-col items-center'}>
							<SdfSpotIllustration
								illustrationName={'empty-inbox'}
								size={'md'}
							/>
							<div className={'mt-5 text-center'}>
								<div
								className={'mt-2 text-md font-bold leading-xs tracking-xs text-tertiary'}
								>
									You haven’t added any additional APIs or event subscriptions to a project yet.
								</div>
								<div
								className={'mt-2 text-sm font-normal leading-xs tracking-xs text-tertiary'}
								>
									When you add an API or event, each selection will be listed here with the ability to commit them to a specific project.
								</div>
							</div>
						</div>
					</div> :
					<div className="my-2 self-end flex justify-end">
						<SdfButton
							emphasis={"secondary"}
							onClick={submitScopeChange}
							busy={isLoading}
							ref={addLinkRef}
							className='relative'
						>
							{needApproval ? "Submit for Approval" : "Commit to Project"}
						</SdfButton>
					</div>
				}
			</SdfBox>
		</div>
	);
};
